import { Button } from '@/components/ui/button';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';


interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewIndicatorScoreDrawer({ open, setOpen }: Props) {
  const org = useStore($org);   

  // Create form schema after we have event types
  const formSchema = z.object({
    organizationId: z.string(),
    accountReceivableId: z.string(),
    currentScore: z.number(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      organizationId: org?.id ?? '',
      accountReceivableId: '',
      currentScore: 0,
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    if (!org?.id) {
      toast.error('No organization selected');
      return;
    }
    
    const result = await createIndicatorScore(
      org.id,
      values.accountReceivableId,
      values.currentScore,
    );
    if (!result?.succeeded) {
      toast('Error creating new indicator score:', { // Updated error message
        description: result?.message,
        duration: 5000,
      });
    }

    setIsLoading(false);
    setOpen(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create new Score Logic</SheetTitle>
          <SheetDescription>
            Fill in the details for the new score logic.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-8">
            <FormField
              control={form.control}
              name="accountReceivableId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Receivable</FormLabel>
                  <FormControl>
                    <Input placeholder="Account Receivable ABC" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the Account Receivable ID to link this score logic to an account receivable.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currentScore"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Score</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Current Score" 
                      {...field}
                      onChange={e => field.onChange(Number(e.target.value))}
                      value={field.value ?? 0} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}